import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { format } from 'date-fns';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Currency } from '../../models/currency.model';
import { ExchangeRateDA } from '../../services/admin/exchange.rate.service';

export interface ForeignCurrencyState {
  enabled: boolean;
  code: string;
  name: string;
  symbol: string;
  exchange_rate: string;
  exchange_date: string;
}

export const foreignCurrencyDefault: ForeignCurrencyState = {
  enabled: false,
  code: '',
  name: '',
  symbol: '',
  exchange_rate: '',
  exchange_date: format(new Date(), 'yyyy-MM-dd')
}

export function foreignCurrencyFromRecord(record: any): ForeignCurrencyState {
  if (record?.foreign_currency) {
    // Backend stores rate as "1 company = X foreign" (e.g. 1 CHF = 1.0975 EUR)
    // Convert back to user format "1 foreign = X company" (e.g. 1 EUR = 0.9111 CHF)
    const storedRate = record.foreign_currency.exchange_rate ?? 0
    const userRate = storedRate > 0 ? Math.round((1 / storedRate) * 1e6) / 1e6 : 0
    return {
      enabled: true,
      code: record.foreign_currency.code,
      name: record.foreign_currency.name,
      symbol: record.foreign_currency.symbol,
      exchange_rate: userRate.toString(),
      exchange_date: record.foreign_currency.exchange_date ? record.foreign_currency.exchange_date.slice(0, 10) : format(new Date(), 'yyyy-MM-dd')
    }
  }
  return { ...foreignCurrencyDefault }
}

export function foreignCurrencyPayload(state: ForeignCurrencyState): any {
  if (!state.enabled || !state.code || !state.exchange_rate) return null
  const userRate = parseFloat(state.exchange_rate)
  if (userRate <= 0) return null
  // User enters "1 foreign = X company" (e.g. 1 EUR = 0.9111 CHF)
  // Backend convention: "1 company = X foreign" (e.g. 1 CHF = 1.0975 EUR)
  const internalRate = 1 / userRate
  return {
    code: state.code,
    name: state.name,
    symbol: state.symbol,
    exchange_rate: internalRate,
    exchange_date: state.exchange_date
  }
}

export function isForeignCurrencyValid(state: ForeignCurrencyState): boolean {
  if (!state.enabled) return true
  return state.code.length > 0 && state.exchange_rate.length > 0 && parseFloat(state.exchange_rate) > 0 && state.exchange_date.length > 0
}

// Canonical conversion helpers — the ONLY place rate math lives outside fromRecord/payload
// state.exchange_rate = "1 foreign = X company" (e.g. 1 EUR = 0.9111 CHF)

/** Convert company amount to foreign: CHF → EUR = CHF / rate (129.72 / 0.9111 = 142.38) */
export function companyToForeign(amountCompany: number, state: ForeignCurrencyState): number {
  const r = parseFloat(state.exchange_rate)
  return r > 0 ? amountCompany / r : 0
}

/** Convert foreign amount to company: EUR → CHF = EUR * rate (142.38 * 0.9111 = 129.72) */
export function foreignToCompany(amountForeign: number, state: ForeignCurrencyState): number {
  const r = parseFloat(state.exchange_rate)
  return amountForeign * r
}

export interface AppInputForeignCurrencyProps {
  companyId: string;
  companyCurrencyCode: string;
  selectedCurrency: Currency | null;
  foreignCurrency: ForeignCurrencyState;
  setForeignCurrency: Dispatch<SetStateAction<ForeignCurrencyState>>;
}

// Not exported — just a small label helper
function rateLabel(foreignCode: string, companyCurrencyCode: string): string {
  return `1 ${foreignCode} = ? ${companyCurrencyCode}`
}

const AppInputForeignCurrency: React.FunctionComponent<AppInputForeignCurrencyProps> = ({companyId, companyCurrencyCode, selectedCurrency, foreignCurrency, setForeignCurrency}) => {
  const {lang} = StateLanguage()

  // Reset state on unmount (when currency goes back to company currency)
  useEffect(() => {
    return () => {
      setForeignCurrency({...foreignCurrencyDefault})
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-fetch rate when selected currency changes
  useEffect(() => {
    if (!selectedCurrency || selectedCurrency.code === companyCurrencyCode) return

    // If already loaded from record with matching code and rate, just ensure enabled
    if (foreignCurrency.code === selectedCurrency.code && foreignCurrency.exchange_rate) {
      if (!foreignCurrency.enabled) {
        setForeignCurrency(prev => ({...prev, enabled: true}))
      }
      return
    }

    // Set currency details and fetch rate
    setForeignCurrency({
      enabled: true,
      code: selectedCurrency.code,
      name: selectedCurrency.name,
      symbol: selectedCurrency.symbol,
      exchange_rate: '',
      exchange_date: format(new Date(), 'yyyy-MM-dd')
    })

    ExchangeRateDA.getRate(companyId, companyCurrencyCode, selectedCurrency.code).then(response => {
      if (response.status === 200) {
        const data = response.data
        // API returns rate already in user convention "1 foreign = X company" (e.g. 1 EUR = 0.9111 CHF)
        setForeignCurrency(prev => ({
          ...prev,
          exchange_rate: data.rate?.toString() ?? '',
          exchange_date: data.date ?? format(new Date(), 'yyyy-MM-dd')
        }))
      }
    })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency?.code])

  const handleChangeRate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForeignCurrency(prev => ({...prev, exchange_rate: event.target.value}))
  }

  const handleChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForeignCurrency(prev => ({...prev, exchange_date: event.target.value}))
  }

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <small className="text-muted d-block mb-1">{rateLabel(foreignCurrency.code || selectedCurrency?.code || '', companyCurrencyCode)}</small>
        <input className="form-control" type="number" step="0.000001" min="0" placeholder="0.000000" value={foreignCurrency.exchange_rate} onChange={handleChangeRate} />
      </div>
      <div className="col-md-6">
        <label className="form-label fs-7 mb-1">{lang.labels.exchangeDate}</label>
        <input className="form-control" type="date" value={foreignCurrency.exchange_date} onChange={handleChangeDate} />
      </div>
    </div>
  )
}

export default AppInputForeignCurrency;
